import React, { ReactNode, useEffect } from "react";
import { Box, Container, Typography, IconButton, Chip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";

interface DetailHeroProps {
  images: string[];
  currentImageIndex: number;
  title: string;
  categoryLabel: string;
  categoryIcon?: ReactNode;
  gradientColors: string;
  statusBadge?:
    | {
        label: string;
        color: string;
      }
    | ReactNode; // Allow both object and ReactNode for flexibility
  metaInfo: ReactNode; // Flexible meta info section (dates, location, etc.)
  onBack: () => void;
  onPrevImage?: () => void;
  onNextImage?: () => void;
}

/**
 * Common hero section for detail screens
 * - Gradient overlay with pattern
 * - Image carousel with navigation
 * - Back button
 * - Category badge
 * - Title and status
 * - Flexible meta info section
 */
export const DetailHero: React.FC<DetailHeroProps> = ({
  images,
  currentImageIndex,
  title,
  categoryLabel,
  categoryIcon,
  gradientColors,
  statusBadge,
  metaInfo,
  onBack,
  onPrevImage,
  onNextImage,
}) => {
  // Auto-carousel: cycle through images every 5 seconds
  useEffect(() => {
    if (images.length > 1 && onNextImage) {
      const interval = setInterval(() => {
        onNextImage();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [images.length, onNextImage, currentImageIndex]);

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: 400, md: 500 },
        overflow: "hidden",
        pt: "env(safe-area-inset-top)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: gradientColors,
          opacity: images.length > 0 ? 0.6 : 0.8,
          zIndex: 1,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)
          `,
          zIndex: 1,
        },
      }}
    >
      {/* Background Image Carousel */}
      {images.length > 0 && (
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImageIndex}
            src={images[currentImageIndex]}
            alt={title}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              pointerEvents: "none",
            }}
          />
        </AnimatePresence>
      )}

      {/* Back Button - Top Left */}
      <IconButton
        onClick={onBack}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 2,
          bgcolor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          "&:hover": {
            bgcolor: "white",
            transform: "scale(1.05)",
          },
          transition: "all 0.2s",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <ArrowBack />
      </IconButton>

      {/* Category Badge - Top Right */}
      <Chip
        icon={categoryIcon ? <span>{categoryIcon}</span> : undefined}
        label={categoryLabel.toUpperCase()}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 2,
          bgcolor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: 0.5,
          px: 1.5,
          height: 32,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      />

      {/* Carousel Dots Indicator */}
      {images.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 20, md: 30 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            display: "flex",
            gap: 1,
            bgcolor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            px: 1.5,
            py: 1,
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={() => {
                // Simple navigation: only go to adjacent images
                if (index === currentImageIndex + 1 && onNextImage) {
                  onNextImage();
                } else if (index === currentImageIndex - 1 && onPrevImage) {
                  onPrevImage();
                } else if (
                  index === 0 &&
                  currentImageIndex === images.length - 1 &&
                  onNextImage
                ) {
                  // Wrap around: last to first
                  onNextImage();
                } else if (
                  index === images.length - 1 &&
                  currentImageIndex === 0 &&
                  onPrevImage
                ) {
                  // Wrap around: first to last
                  onPrevImage();
                }
              }}
              sx={{
                width: index === currentImageIndex ? 24 : 8,
                height: 8,
                borderRadius: 1,
                bgcolor:
                  index === currentImageIndex
                    ? "white"
                    : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "white",
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Hero Content */}
      <Container
        maxWidth="lg"
        sx={{
          position: "absolute",
          bottom: 80,
          left: 0,
          right: 0,
          zIndex: 2,
          px: { xs: 2, md: 3 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Status Badge (Optional) */}
          {statusBadge && (
            <Box sx={{ mb: 2 }}>
              {typeof statusBadge === "object" &&
              statusBadge !== null &&
              "label" in statusBadge &&
              "color" in statusBadge ? (
                <Chip
                  label={statusBadge.label as string}
                  sx={{
                    bgcolor: `${statusBadge.color as string}.main`,
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    px: 2,
                    height: 36,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  }}
                />
              ) : (
                statusBadge
              )}
            </Box>
          )}

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "white",
              mb: 2,
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {/* Meta Info (Flexible content passed by parent) */}
          {metaInfo}
        </motion.div>
      </Container>
    </Box>
  );
};
